import { useEffect, useState } from "react";
import {
  X,
  Globe,
  Warehouse,
  Building2,
  DollarSign,
  Tag,
  Trash2,
} from "lucide-react";

// ─── Shared ───────────────────────────────────────────────────────────────────

function ModalShell({ open, onClose, icon: Icon, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
          <div className="flex items-center justify-between border-b border-slate-100 p-4">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-slate-700" />
              <span className="font-semibold text-slate-900">{title}</span>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 hover:bg-slate-100"
            >
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid gap-1.5">
      <label className="text-sm text-slate-600">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200";
const selectCls =
  "rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200 bg-white";

function ModalActions({ onClose, submitLabel = "Save" }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
      >
        Cancel
      </button>
      <button
        type="submit"
        className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
      >
        {submitLabel}
      </button>
    </div>
  );
}

// ─── Delete Confirm ───────────────────────────────────────────────────────────

export function DeleteConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-rose-50 p-2">
              <Trash2 className="h-5 w-5 text-rose-500" />
            </div>
            <h2 className="font-semibold text-slate-900">{title}</h2>
          </div>
          <p className="text-sm text-slate-500">{message}</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Country Modals ───────────────────────────────────────────────────────────

export function CreateCountryModal({ open, onClose, onCreate }) {
  const [form, setForm] = useState({ name: "", code: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ name: "", code: "" });
    setError("");
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Country name is required.");
    if (!form.code.trim()) return setError("Country code is required.");
    onCreate({ name: form.name.trim(), code: form.code.trim().toUpperCase() });
  };

  return (
    <ModalShell open={open} onClose={onClose} icon={Globe} title="Add Country">
      <form onSubmit={submit} className="p-4 space-y-4">
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Country Name">
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="China"
          />
        </Field>
        <Field label="Country Code (ISO)">
          <input
            value={form.code}
            onChange={(e) => set("code", e.target.value)}
            className={inputCls}
            placeholder="CN"
            maxLength={3}
          />
        </Field>
        <ModalActions onClose={onClose} submitLabel="Add Country" />
      </form>
    </ModalShell>
  );
}

export function EditCountryModal({ open, onClose, onUpdate, country }) {
  const [form, setForm] = useState({ name: "", code: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !country) return;
    setForm({ name: country.name, code: country.code });
    setError("");
  }, [open, country]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Country name is required.");
    if (!form.code.trim()) return setError("Country code is required.");
    onUpdate({ name: form.name.trim(), code: form.code.trim().toUpperCase() });
  };

  return (
    <ModalShell open={open} onClose={onClose} icon={Globe} title="Edit Country">
      <form onSubmit={submit} className="p-4 space-y-4">
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Country Name">
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="China"
          />
        </Field>
        <Field label="Country Code (ISO)">
          <input
            value={form.code}
            onChange={(e) => set("code", e.target.value)}
            className={inputCls}
            placeholder="CN"
            maxLength={3}
          />
        </Field>
        <ModalActions onClose={onClose} submitLabel="Save Changes" />
      </form>
    </ModalShell>
  );
}

// ─── Warehouse Modals ─────────────────────────────────────────────────────────

const emptyWarehouse = {
  name: "",
  contactPerson: "",
  phone: "",
  countryId: "",
  city: "",
  area: "",
  zipCode: "",
  street: "",
  shippingMark: "",
};

export function CreateWarehouseModal({
  open,
  onClose,
  onCreate,
  countries = [],
}) {
  const [form, setForm] = useState(emptyWarehouse);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(emptyWarehouse);
    setError("");
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Warehouse name is required.");
    if (!form.countryId) return setError("Country is required.");
    if (!form.contactPerson.trim())
      return setError("Contact person is required.");
    if (!form.phone.trim()) return setError("Phone is required.");
    if (!form.city.trim()) return setError("City is required.");
    if (!form.street.trim()) return setError("Street is required.");
    onCreate({ ...form, countryId: Number(form.countryId) });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={Warehouse}
      title="Create Warehouse"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[70vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Warehouse Name">
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="Main Warehouse"
          />
        </Field>
        <Field label="Country">
          <select
            value={form.countryId}
            onChange={(e) => set("countryId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Contact Person">
            <input
              value={form.contactPerson}
              onChange={(e) => set("contactPerson", e.target.value)}
              className={inputCls}
              placeholder="John Doe"
            />
          </Field>
          <Field label="Phone">
            <input
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className={inputCls}
              placeholder="+8801..."
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="City">
            <input
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
              className={inputCls}
              placeholder="Guangzhou"
            />
          </Field>
          <Field label="Area">
            <input
              value={form.area}
              onChange={(e) => set("area", e.target.value)}
              className={inputCls}
              placeholder="Baiyun"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Zip Code">
            <input
              value={form.zipCode}
              onChange={(e) => set("zipCode", e.target.value)}
              className={inputCls}
              placeholder="510000"
            />
          </Field>
          <Field label="Shipping Mark">
            <input
              value={form.shippingMark}
              onChange={(e) => set("shippingMark", e.target.value)}
              className={inputCls}
              placeholder="ASB-GZ"
            />
          </Field>
        </div>
        <Field label="Street">
          <input
            value={form.street}
            onChange={(e) => set("street", e.target.value)}
            className={inputCls}
            placeholder="123 Main St"
          />
        </Field>
        <ModalActions onClose={onClose} submitLabel="Create Warehouse" />
      </form>
    </ModalShell>
  );
}

export function EditWarehouseModal({
  open,
  onClose,
  onUpdate,
  warehouse,
  countries = [],
}) {
  const [form, setForm] = useState(emptyWarehouse);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !warehouse) return;
    setForm({
      name: warehouse.name || "",
      contactPerson: warehouse.contactPerson || "",
      phone: warehouse.phone || "",
      countryId: warehouse.countryId || "",
      city: warehouse.city || "",
      area: warehouse.area || "",
      zipCode: warehouse.zipCode || "",
      street: warehouse.street || "",
      shippingMark: warehouse.shippingMark || "",
    });
    setError("");
  }, [open, warehouse]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Warehouse name is required.");
    if (!form.countryId) return setError("Country is required.");
    onUpdate({ ...form, countryId: Number(form.countryId) });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={Warehouse}
      title="Edit Warehouse"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[70vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Warehouse Name">
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="Main Warehouse"
          />
        </Field>
        <Field label="Country">
          <select
            value={form.countryId}
            onChange={(e) => set("countryId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Contact Person">
            <input
              value={form.contactPerson}
              onChange={(e) => set("contactPerson", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Phone">
            <input
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="City">
            <input
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Area">
            <input
              value={form.area}
              onChange={(e) => set("area", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Zip Code">
            <input
              value={form.zipCode}
              onChange={(e) => set("zipCode", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Shipping Mark">
            <input
              value={form.shippingMark}
              onChange={(e) => set("shippingMark", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Street">
          <input
            value={form.street}
            onChange={(e) => set("street", e.target.value)}
            className={inputCls}
          />
        </Field>
        <ModalActions onClose={onClose} submitLabel="Save Changes" />
      </form>
    </ModalShell>
  );
}

// ─── Shipping Company Modals ──────────────────────────────────────────────────

const emptyCompany = {
  name: "",
  type: "",
  mode: "",
  deliveryTimeMin: "",
  deliveryTimeMax: "",
  countryId: "",
  agentWarehouseId: "",
  destinationWarehouseId: "",
};

export function CreateShippingCompanyModal({
  open,
  onClose,
  onCreate,
  countries = [],
  warehouses = [],
}) {
  const [form, setForm] = useState(emptyCompany);
  const [error, setError] = useState("");
  const [instructions, setInstructions] = useState([]);
  const [instructionInput, setInstructionInput] = useState("");

  const addInstruction = () => {
    if (!instructionInput.trim()) return;
    setInstructions((prev) => [...prev, instructionInput.trim()]);
    setInstructionInput("");
  };

  const removeInstruction = (index) => {
    setInstructions((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!open) return;
    setForm(emptyCompany);
    setInstructions([]); // ← add
    setInstructionInput(""); // ← add
    setError("");
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Company name is required.");
    if (!form.type.trim()) return setError("Type is required.");
    if (!form.mode.trim()) return setError("Mode is required.");
    if (!form.countryId) return setError("Country is required.");
    if (!form.agentWarehouseId) return setError("Agent warehouse is required.");
    if (!form.destinationWarehouseId)
      return setError("Destination warehouse is required.");
    if (!form.deliveryTimeMin || !form.deliveryTimeMax)
      return setError("Delivery time range is required.");
    onCreate({
      ...form,
      countryId: Number(form.countryId),
      agentWarehouseId: Number(form.agentWarehouseId),
      destinationWarehouseId: Number(form.destinationWarehouseId),
      deliveryTimeMin: Number(form.deliveryTimeMin),
      deliveryTimeMax: Number(form.deliveryTimeMax),
      instructions, // ← add
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={Building2}
      title="Create Shipping Company"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[70vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Company Name">
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="ASB Cargo"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => set("type", e.target.value)}
              className={selectCls}
            >
              <option value="">Select type</option>
              <option value="AIR">Air</option>
              <option value="SEA">Sea</option>
              <option value="LAND">Land</option>
            </select>
          </Field>
          <Field label="Mode">
            <select
              value={form.mode}
              onChange={(e) => set("mode", e.target.value)}
              className={selectCls}
            >
              <option value="">Select mode</option>
              <option value="EXPRESS">Express</option>
              <option value="STANDARD">Standard</option>
              <option value="ECONOMY">Economy</option>
            </select>
          </Field>
        </div>
        <Field label="Country">
          <select
            value={form.countryId}
            onChange={(e) => set("countryId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Delivery Time Min (days)">
            <input
              type="number"
              min={1}
              value={form.deliveryTimeMin}
              onChange={(e) => set("deliveryTimeMin", e.target.value)}
              className={inputCls}
              placeholder="3"
            />
          </Field>
          <Field label="Delivery Time Max (days)">
            <input
              type="number"
              min={1}
              value={form.deliveryTimeMax}
              onChange={(e) => set("deliveryTimeMax", e.target.value)}
              className={inputCls}
              placeholder="7"
            />
          </Field>
        </div>
        <Field label="Agent Warehouse">
          <select
            value={form.agentWarehouseId}
            onChange={(e) => set("agentWarehouseId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select agent warehouse</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.city}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Destination Warehouse">
          <select
            value={form.destinationWarehouseId}
            onChange={(e) => set("destinationWarehouseId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select destination warehouse</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.city}
              </option>
            ))}
          </select>
        </Field>
        {/* Instructions */}
        <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-800">Instructions</p>
          <div className="flex gap-2">
            <input
              value={instructionInput}
              onChange={(e) => setInstructionInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && (e.preventDefault(), addInstruction())
              }
              className={inputCls + " flex-1"}
              placeholder="e.g. Do not ship liquids without approval"
            />
            <button
              type="button"
              onClick={addInstruction}
              className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
            >
              Add
            </button>
          </div>

          {instructions.length > 0 && (
            <div className="space-y-2">
              {instructions.map((ins, i) => (
                <div
                  key={i}
                  className="flex items-start justify-between rounded-xl bg-slate-50 px-3 py-2"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold text-slate-400 mt-0.5">
                      {i + 1}.
                    </span>
                    <p className="text-sm text-slate-700">{ins}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeInstruction(i)}
                    className="ml-2 text-rose-400 hover:text-rose-600 text-xs"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <ModalActions onClose={onClose} submitLabel="Create Company" />
      </form>
    </ModalShell>
  );
}

export function EditShippingCompanyModal({
  open,
  onClose,
  onUpdate,
  company,
  countries = [],
  warehouses = [],
}) {
  const [form, setForm] = useState(emptyCompany);
  const [error, setError] = useState("");
  const [instructions, setInstructions] = useState([]);
  const [instructionInput, setInstructionInput] = useState("");

  const addInstruction = () => {
    if (!instructionInput.trim()) return;
    setInstructions((prev) => [...prev, instructionInput.trim()]);
    setInstructionInput("");
  };

  const removeInstruction = (index) => {
    setInstructions((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!open || !company) return;
    setForm({
      name: company.name || "",
      type: company.type || "",
      mode: company.mode || "",
      deliveryTimeMin: company.deliveryTimeMin || "",
      deliveryTimeMax: company.deliveryTimeMax || "",
      countryId: company.countryId || "",
      agentWarehouseId: company.agentWarehouseId || "",
      destinationWarehouseId: company.destinationWarehouseId || "",
    });
    setInstructions(company.instructions?.map((i) => i.instruction) || []);
    setInstructionInput("");
    setError("");
  }, [open, company]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Company name is required.");
    onUpdate({
      ...form,
      countryId: Number(form.countryId),
      agentWarehouseId: Number(form.agentWarehouseId),
      destinationWarehouseId: Number(form.destinationWarehouseId),
      deliveryTimeMin: Number(form.deliveryTimeMin),
      deliveryTimeMax: Number(form.deliveryTimeMax),
      instructions,
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={Building2}
      title="Edit Shipping Company"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[70vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Company Name">
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <select
              value={form.type}
              onChange={(e) => set("type", e.target.value)}
              className={selectCls}
            >
              <option value="">Select type</option>
              <option value="AIR">Air</option>
              <option value="SEA">Sea</option>
              <option value="LAND">Land</option>
            </select>
          </Field>
          <Field label="Mode">
            <select
              value={form.mode}
              onChange={(e) => set("mode", e.target.value)}
              className={selectCls}
            >
              <option value="">Select mode</option>
              <option value="EXPRESS">Express</option>
              <option value="STANDARD">Standard</option>
              <option value="ECONOMY">Economy</option>
            </select>
          </Field>
        </div>
        <Field label="Country">
          <select
            value={form.countryId}
            onChange={(e) => set("countryId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select country</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Delivery Time Min (days)">
            <input
              type="number"
              min={1}
              value={form.deliveryTimeMin}
              onChange={(e) => set("deliveryTimeMin", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Delivery Time Max (days)">
            <input
              type="number"
              min={1}
              value={form.deliveryTimeMax}
              onChange={(e) => set("deliveryTimeMax", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Agent Warehouse">
          <select
            value={form.agentWarehouseId}
            onChange={(e) => set("agentWarehouseId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select agent warehouse</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.city}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Destination Warehouse">
          <select
            value={form.destinationWarehouseId}
            onChange={(e) => set("destinationWarehouseId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select destination warehouse</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} — {w.city}
              </option>
            ))}
          </select>
        </Field>
        {/* Instructions */}
        <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-800">Instructions</p>
          <div className="flex gap-2">
            <input
              value={instructionInput}
              onChange={(e) => setInstructionInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && (e.preventDefault(), addInstruction())
              }
              className={inputCls + " flex-1"}
              placeholder="e.g. Do not ship liquids without approval"
            />
            <button
              type="button"
              onClick={addInstruction}
              className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
            >
              Add
            </button>
          </div>

          {instructions.length > 0 && (
            <div className="space-y-2">
              {instructions.map((ins, i) => (
                <div
                  key={i}
                  className="flex items-start justify-between rounded-xl bg-slate-50 px-3 py-2"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold text-slate-400 mt-0.5">
                      {i + 1}.
                    </span>
                    <p className="text-sm text-slate-700">{ins}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeInstruction(i)}
                    className="ml-2 text-rose-400 hover:text-rose-600 text-xs"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <ModalActions onClose={onClose} submitLabel="Save Changes" />
      </form>
    </ModalShell>
  );
}

// ─── Price Modals ─────────────────────────────────────────────────────────────

const emptyPrice = {
  shippingCompanyId: "",
  rate: "",
  currency: "CNY",
  unit: "KG",
  contains: "REGULAR",
  shipmentType: "",
  minWeight: "",
  maxWeight: "",
  minimumCharge: "",
  description: "",
};

export function CreatePriceModal({ open, onClose, onCreate, companies = [] }) {
  const [form, setForm] = useState(emptyPrice);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(emptyPrice);
    setError("");
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.shippingCompanyId)
      return setError("Shipping company is required.");
    if (!form.rate) return setError("Rate is required.");
    if (!form.currency.trim()) return setError("Currency is required.");
    onCreate({
      ...form,
      shippingCompanyId: Number(form.shippingCompanyId),
      rate: parseFloat(form.rate),
      minWeight: form.minWeight ? parseFloat(form.minWeight) : null,
      maxWeight: form.maxWeight ? parseFloat(form.maxWeight) : null,
      minimumCharge: form.minimumCharge ? parseFloat(form.minimumCharge) : null,
      shipmentType: form.shipmentType || null,
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={DollarSign}
      title="Create Price"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[70vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Shipping Company">
          <select
            value={form.shippingCompanyId}
            onChange={(e) => set("shippingCompanyId", e.target.value)}
            className={selectCls}
          >
            <option value="">Select company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Rate">
            <input
              type="number"
              step="0.01"
              min={0}
              value={form.rate}
              onChange={(e) => set("rate", e.target.value)}
              className={inputCls}
              placeholder="5.00"
            />
          </Field>
          <Field label="Currency">
            <select
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
              className={selectCls}
            >
              <option value="CNY">CNY</option>
              <option value="USD">USD</option>
              <option value="BDT">BDT</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Unit">
            <select
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
              className={selectCls}
            >
              <option value="KG">KG</option>
              <option value="CBM">CBM</option>
              <option value="PCS">PCS</option>
            </select>
          </Field>
          <Field label="Contains">
            <select
              value={form.contains}
              onChange={(e) => set("contains", e.target.value)}
              className={selectCls}
            >
              <option value="REGULAR">Regular</option>
              <option value="LIQUID">Liquid</option>
              <option value="POWDER">Powder</option>
              <option value="BATTERY">Battery</option>
            </select>
          </Field>
        </div>
        <Field label="Shipment Type (optional)">
          <select
            value={form.shipmentType}
            onChange={(e) => set("shipmentType", e.target.value)}
            className={selectCls}
          >
            <option value="">None</option>
            <option value="GZ">GZ (Guangzhou)</option>
            <option value="HK">HK (Hong Kong)</option>
            <option value="EXP">EXP (Express)</option>
            <option value="OTHER">OTH (Other)</option>
            <option value="UKR">UKR (United Kingdom Regular)</option>
            <option value="UKS">UKS (United Kingdom Sensetive)</option>
            <option value="UKX">UKX (United Kingdom Express)</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Min Weight (optional)">
            <input
              type="number"
              step="0.01"
              min={0}
              value={form.minWeight}
              onChange={(e) => set("minWeight", e.target.value)}
              className={inputCls}
              placeholder="0"
            />
          </Field>
          <Field label="Max Weight (optional)">
            <input
              type="number"
              step="0.01"
              min={0}
              value={form.maxWeight}
              onChange={(e) => set("maxWeight", e.target.value)}
              className={inputCls}
              placeholder="100"
            />
          </Field>
        </div>
        <Field label="Minimum Charge (optional)">
          <input
            type="number"
            step="0.01"
            min={0}
            value={form.minimumCharge}
            onChange={(e) => set("minimumCharge", e.target.value)}
            className={inputCls}
            placeholder="10.00"
          />
        </Field>
        <Field label="Description (optional)">
          <input
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className={inputCls}
            placeholder="e.g. GZ flat rate for regular items"
          />
        </Field>
        <ModalActions onClose={onClose} submitLabel="Create Price" />
      </form>
    </ModalShell>
  );
}

export function EditPriceModal({
  open,
  onClose,
  onUpdate,
  price,
  companies = [],
}) {
  const [form, setForm] = useState(emptyPrice);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !price) return;
    setForm({
      shippingCompanyId: price.shippingCompanyId || "",
      rate: price.rate || "",
      currency: price.currency || "CNY",
      unit: price.unit || "KG",
      contains: price.contains || "REGULAR",
      shipmentType: price.shipmentType || "",
      minWeight: price.minWeight || "",
      maxWeight: price.maxWeight || "",
      minimumCharge: price.minimumCharge || "",
      description: price.description || "",
    });
    setError("");
  }, [open, price]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.rate) return setError("Rate is required.");
    onUpdate({
      ...form,
      shippingCompanyId: Number(form.shippingCompanyId),
      rate: parseFloat(form.rate),
      minWeight: form.minWeight ? parseFloat(form.minWeight) : null,
      maxWeight: form.maxWeight ? parseFloat(form.maxWeight) : null,
      minimumCharge: form.minimumCharge ? parseFloat(form.minimumCharge) : null,
      shipmentType: form.shipmentType || null,
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={DollarSign}
      title="Edit Price"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[70vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Shipping Company">
          <select
            value={form.shippingCompanyId}
            onChange={(e) => set("shippingCompanyId", e.target.value)}
            className={selectCls}
            disabled
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Rate">
            <input
              type="number"
              step="0.01"
              min={0}
              value={form.rate}
              onChange={(e) => set("rate", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Currency">
            <select
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
              className={selectCls}
            >
              <option value="CNY">CNY</option>
              <option value="USD">USD</option>
              <option value="BDT">BDT</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Unit">
            <select
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
              className={selectCls}
            >
              <option value="KG">KG</option>
              <option value="CBM">CBM</option>
              <option value="PCS">PCS</option>
            </select>
          </Field>
          <Field label="Contains">
            <select
              value={form.contains}
              onChange={(e) => set("contains", e.target.value)}
              className={selectCls}
            >
              <option value="REGULAR">Regular</option>
              <option value="LIQUID">Liquid</option>
              <option value="POWDER">Powder</option>
              <option value="BATTERY">Battery</option>
            </select>
          </Field>
        </div>
        <Field label="Shipment Type (optional)">
          <select
            value={form.shipmentType}
            onChange={(e) => set("shipmentType", e.target.value)}
            className={selectCls}
          >
            <option value="">None</option>
            <option value="GZ">GZ (Guangzhou)</option>
            <option value="HK">HK (Hong Kong)</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Min Weight">
            <input
              type="number"
              step="0.01"
              value={form.minWeight}
              onChange={(e) => set("minWeight", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Max Weight">
            <input
              type="number"
              step="0.01"
              value={form.maxWeight}
              onChange={(e) => set("maxWeight", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Minimum Charge">
          <input
            type="number"
            step="0.01"
            value={form.minimumCharge}
            onChange={(e) => set("minimumCharge", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Description">
          <input
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className={inputCls}
          />
        </Field>
        <ModalActions onClose={onClose} submitLabel="Save Changes" />
      </form>
    </ModalShell>
  );
}

// ─── Shipping Category Modals ─────────────────────────────────────────────────

function slugify(input = "") {
  return input
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const emptyShippingCategory = {
  name: "",
  slug: "",
  description: "",
  sortOrder: 0,
  rates: [],
};

export function CreateShippingCategoryModal({
  open,
  onClose,
  onCreate,
  companies = [],
  prices = [],
  onCompanyChange,
}) {
  const [form, setForm] = useState(emptyShippingCategory);
  const [autoSlug, setAutoSlug] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedPriceId, setSelectedPriceId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(emptyShippingCategory);
    setAutoSlug(true);
    setSelectedCompanyId("");
    setSelectedPriceId("");
    setError("");
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleNameChange = (v) => {
    set("name", v);
    if (autoSlug) set("slug", slugify(v));
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedPriceId("");
    if (companyId && onCompanyChange) onCompanyChange(Number(companyId));
  };

  const addRate = () => {
    if (!selectedCompanyId || !selectedPriceId) return;
    const exists = form.rates.find(
      (r) =>
        r.shippingCompanyId === Number(selectedCompanyId) &&
        r.priceId === Number(selectedPriceId),
    );
    if (exists) return setError("This rate is already added.");
    setError("");
    set("rates", [
      ...form.rates,
      {
        shippingCompanyId: Number(selectedCompanyId),
        priceId: Number(selectedPriceId),
      },
    ]);
    setSelectedPriceId("");
  };

  const removeRate = (index) => {
    set(
      "rates",
      form.rates.filter((_, i) => i !== index),
    );
  };

  const getPriceLabel = (priceId) => {
    const p = prices.find((p) => p.id === priceId);
    if (!p) return priceId;
    return `${p.contains} — ${p.rate} ${p.currency}/${p.unit}${p.shipmentType ? ` (${p.shipmentType})` : ""}`;
  };

  const getCompanyName = (companyId) => {
    return companies.find((c) => c.id === companyId)?.name || companyId;
  };

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Category name is required.");
    if (!form.slug.trim()) return setError("Slug is required.");
    onCreate({ ...form, sortOrder: Number(form.sortOrder) });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={Tag}
      title="Create Shipping Category"
    >
      <form
        onSubmit={submit}
        className="p-4 space-y-4 max-h-[75vh] overflow-y-auto"
      >
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Category Name">
          <input
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className={inputCls}
            placeholder="Mobile"
          />
        </Field>
        <Field label="Slug">
          <div className="flex items-center gap-2">
            <input
              value={form.slug}
              onChange={(e) => {
                setAutoSlug(false);
                set("slug", e.target.value);
              }}
              className={inputCls + " flex-1"}
              placeholder="mobile"
            />
            <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
              <input
                type="checkbox"
                checked={autoSlug}
                onChange={(e) => setAutoSlug(e.target.checked)}
              />
              Auto
            </label>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Description (optional)">
            <input
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className={inputCls}
              placeholder="Mobile phones, tablets..."
            />
          </Field>
          <Field label="Sort Order">
            <input
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) => set("sortOrder", e.target.value)}
              className={inputCls}
              placeholder="0"
            />
          </Field>
        </div>

        {/* Rate Attachment */}
        <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-800">Attach Rates</p>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={selectedCompanyId}
              onChange={(e) => handleCompanyChange(e.target.value)}
              className={selectCls}
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={selectedPriceId}
              onChange={(e) => setSelectedPriceId(e.target.value)}
              className={selectCls}
              disabled={!selectedCompanyId}
            >
              <option value="">Select price</option>
              {prices
                .filter(
                  (p) => p.shippingCompanyId === Number(selectedCompanyId),
                )
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.contains} — {p.rate} {p.currency}/{p.unit}
                    {p.shipmentType ? ` (${p.shipmentType})` : ""}
                  </option>
                ))}
            </select>
          </div>
          <button
            type="button"
            onClick={addRate}
            className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
          >
            + Add Rate
          </button>

          {form.rates.length > 0 && (
            <div className="space-y-2 pt-1">
              {form.rates.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs"
                >
                  <span className="text-slate-700">
                    <span className="font-medium">
                      {getCompanyName(r.shippingCompanyId)}
                    </span>
                    {" · "}
                    {getPriceLabel(r.priceId)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRate(i)}
                    className="text-rose-400 hover:text-rose-600"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <ModalActions onClose={onClose} submitLabel="Create Category" />
      </form>
    </ModalShell>
  );
}

export function EditShippingCategoryModal({
  open,
  onClose,
  onUpdate,
  shippingCategory,
}) {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    sortOrder: 0,
    isActive: true,
  });
  const [autoSlug, setAutoSlug] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !shippingCategory) return;
    setForm({
      name: shippingCategory.name || "",
      slug: shippingCategory.slug || "",
      description: shippingCategory.description || "",
      sortOrder: shippingCategory.sortOrder || 0,
      isActive: shippingCategory.isActive ?? true,
    });
    setError("");
  }, [open, shippingCategory]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleNameChange = (v) => {
    set("name", v);
    if (autoSlug) set("slug", slugify(v));
  };

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Category name is required.");
    if (!form.slug.trim()) return setError("Slug is required.");
    onUpdate({ ...form, sortOrder: Number(form.sortOrder) });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon={Tag}
      title="Edit Shipping Category"
    >
      <form onSubmit={submit} className="p-4 space-y-4">
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
        <Field label="Category Name">
          <input
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Slug">
          <div className="flex items-center gap-2">
            <input
              value={form.slug}
              onChange={(e) => {
                setAutoSlug(false);
                set("slug", e.target.value);
              }}
              className={inputCls + " flex-1"}
            />
            <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
              <input
                type="checkbox"
                checked={autoSlug}
                onChange={(e) => setAutoSlug(e.target.checked)}
              />
              Auto
            </label>
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Description">
            <input
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Sort Order">
            <input
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) => set("sortOrder", e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Status">
          <select
            value={form.isActive ? "true" : "false"}
            onChange={(e) => set("isActive", e.target.value === "true")}
            className={selectCls}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </Field>
        <ModalActions onClose={onClose} submitLabel="Save Changes" />
      </form>
    </ModalShell>
  );
}
